import centerOfMass from "@turf/center-of-mass";
import distance from "@turf/distance";
import { featureCollection, point } from "@turf/helpers";
import transformRotate from "@turf/transform-rotate";
import transformScale from "@turf/transform-scale";

import { setCurrentFloorID } from "@/features/cartography/cartographySlice";
import {
  setPathsEditorSelectedFeatureId,
  storePathsChanges,
} from "@/features/editor/editorSlice";

import type { UndoableCommitOptions } from "@/app/libs/undoable";
import store from "@/app/store";
import { selectCurrentBuilding } from "@/features/cartography/cartographySlice.selectors";
import { generateLinkIDFromNodes } from "@/features/editor/components/paths/utils";
import { selectActivePathsDict } from "@/features/editor/editorSlice.selectors";
import type { EditorChanges, PathProblem } from "@/features/editor/types";
import { updateMapViewState } from "@/features/map/actions";
import type { DeepPartial } from "@/features/utils/genericity";
import { type Building, type Floor, LatLng } from "@/libs/models";
import {
  type PathDict,
  type PathDictGenericItem,
  type PathDictLinkItem,
  type PathDictNodeItem,
  pathDictToPaths,
} from "@/libs/models/path";

/**
 * Update any data of a path item
 *
 * @param itemId The id of the item to update
 * @param item The new data for the item
 */
export const updatePathItem = (
  itemId: string,
  item: DeepPartial<PathDictGenericItem>,
  commitOptions?: UndoableCommitOptions,
) => {
  const changes: EditorChanges<PathDictGenericItem> = {
    [itemId]: {
      item: item,
      type: "updated",
    },
  };
  store.dispatch(storePathsChanges({ changes, commitOptions }));
};

/**
 * Delete either a link or a node, deleting its associated links as well.
 *
 * @param item The item to delete. Can be a link or a node.
 */
export const deletePathItem = (
  itemId: PathDictGenericItem["id"],
  commitOptions?: UndoableCommitOptions,
) => {
  const pathsDict = selectActivePathsDict(store.getState());
  const item = pathsDict[itemId];
  const changes: EditorChanges<PathDictGenericItem> = {};
  changes[item.id] = { type: "deleted" };

  if (item.type == "node") {
    changes[item.id] = { type: "deleted" };
    // Delete associated links
    Object.entries(pathsDict).forEach(([id, x]) => {
      if (x.type == "link" && (x.source == item.id || x.target == item.id)) {
        changes[id] = { type: "deleted" };
      }
    });
  }

  store.dispatch(storePathsChanges({ changes, commitOptions }));
  store.dispatch(setPathsEditorSelectedFeatureId(null));
};

/**
 * Remove a tag from a link
 *
 * @param itemId The ID of the item to modify.
 * @param tagToRemove The tag to remove.
 */
export const removePathLinkTag = (
  itemId: string,
  tagToRemove: string,
  commitOptions?: UndoableCommitOptions,
) => {
  const pathDict = selectActivePathsDict(store.getState());
  const previousTags = (pathDict[itemId] as PathDictLinkItem)?.tags ?? [];

  updatePathItem(
    itemId,
    {
      tags: previousTags.filter((tag) => tag !== tagToRemove),
    },
    commitOptions,
  );
};

/**
 * Add a tag to a link
 *
 * @param itemId The ID of the item to modify.
 * @param newTag The tag to add.
 */
export const addPathLinkTag = (
  itemId: string,
  newTag: string,
  commitOptions?: UndoableCommitOptions,
) => {
  const pathDict = selectActivePathsDict(store.getState());
  const previousTags = (pathDict[itemId] as PathDictLinkItem)?.tags ?? [];

  updatePathItem(
    itemId,
    {
      tags: [...previousTags, newTag],
    },
    commitOptions,
  );
};

/**
 * Update a path node's coordinates based on its ID and new coordinates.
 * Handles transforming the coordinates to cartesian data.
 *
 * @param itemId The id of the path node to update
 * @param newCoordinates The new coordinates for the path node
 */
const updatePathNodeCoordinates = (
  itemId: string,
  newCoordinates: { lat: number; lng: number },
  commitOptions?: UndoableCommitOptions,
) => {
  const currentBuilding = selectCurrentBuilding(store.getState());
  const cartesian =
    currentBuilding.coordinateConverterFlatEarth.toCartesian(newCoordinates);

  updatePathItem(
    itemId,
    { ...newCoordinates, x: cartesian.x, y: cartesian.y },
    commitOptions,
  );
};

/**
 * Moves path node coordinates based on a lat lng displacement
 *
 * @param itemId The id of the path node to update
 * @param newCoordinates The displacement coordinates
 */
export const movePathNodeCoordinates = (
  itemId: string,
  displacement: { lat: number; lng: number },
  commitOptions?: UndoableCommitOptions,
) => {
  const pathsDict = selectActivePathsDict(store.getState());

  const feature = pathsDict[itemId];
  if (feature.type !== "node") return;

  const newCoordinates = {
    lat: feature.lat + displacement.lat,
    lng: feature.lng + displacement.lng,
  };

  updatePathNodeCoordinates(itemId, newCoordinates, commitOptions);
};

/**
 * Update the coordinates of the whole path dict based on a displacement
 * in lat-lng
 */
export const batchUpdatePathDictCoordinates = (
  displacement: {
    lat: number;
    lng: number;
  },
  commitOptions?: UndoableCommitOptions,
) => {
  const pathDict = selectActivePathsDict(store.getState());
  const currentBuilding = selectCurrentBuilding(store.getState());
  const changes: EditorChanges<PathDictGenericItem> = {};

  Object.values(pathDict).forEach((item) => {
    if (item.type !== "node") return;
    const newCoordinates = {
      lat: item.lat - displacement.lat,
      lng: item.lng - displacement.lng,
    };
    const cartesian =
      currentBuilding.coordinateConverterFlatEarth.toCartesian(newCoordinates);
    changes[item.id] = {
      item: {
        lat: newCoordinates.lat,
        lng: newCoordinates.lng,
        x: cartesian.x,
        y: cartesian.y,
      },
      type: "updated",
    };
  });

  store.dispatch(storePathsChanges({ changes, commitOptions }));
};

/**
 * Create a isolated node
 *
 * @param nodeId
 * @param nodePosition
 * @param floorId
 */
export const createNode = (
  nodeId: PathDictNodeItem["id"],
  nodePosition: { lat: number; lng: number },
  floorId: number,
  commitOptions?: UndoableCommitOptions,
) => {
  const currentBuilding = selectCurrentBuilding(store.getState());
  const cartesian =
    currentBuilding.coordinateConverterFlatEarth.toCartesian(nodePosition);

  const changes: EditorChanges<PathDictGenericItem> = {
    [nodeId]: {
      item: {
        floorId: floorId,
        id: nodeId,
        lat: nodePosition.lat,
        lng: nodePosition.lng,
        type: "node",
        x: cartesian.x,
        y: cartesian.y,
      },
      type: "new",
    },
  };

  store.dispatch(storePathsChanges({ changes, commitOptions }));
};

/**
 * Creates a new node, splitting an existing link in two
 *
 * @param nodeId The ID of the node to create
 * @param nodePosition The position of the node to create
 * @param floorId The floor ID of the node to create
 * @param link The link to split
 * @param options Additional options for the node creation.
 *        @param additionalSourceNode If provided, a link will be created from this node to the created one
 */
export const createNodeSplittingLink = (
  nodeId: PathDictNodeItem["id"],
  nodePosition: { lat: number; lng: number },
  floorId: number,
  link: PathDictLinkItem,
  options?: {
    additionalSourceNode?: PathDictNodeItem;
  },
  commitOptions?: UndoableCommitOptions,
) => {
  const changes: EditorChanges<PathDictGenericItem> = {};

  const currentBuilding = selectCurrentBuilding(store.getState());
  const cartesian =
    currentBuilding.coordinateConverterFlatEarth.toCartesian(nodePosition);

  // Create node
  changes[nodeId] = {
    item: {
      floorId: floorId,
      id: nodeId,
      lat: nodePosition.lat,
      lng: nodePosition.lng,
      type: "node",
      x: cartesian.x,
      y: cartesian.y,
    },
    type: "new",
  };

  // Create link from original node to new node
  if (options?.additionalSourceNode) {
    const linkNodeToNewNode = generateLinkIDFromNodes(
      options.additionalSourceNode.id,
      nodeId,
    );
    changes[linkNodeToNewNode] = {
      item: {
        accessible: true,
        floorId: floorId,
        id: linkNodeToNewNode,
        origin: "both",
        source: options.additionalSourceNode.id,
        tags: [],
        target: nodeId,
        type: "link",
      },
      type: "new",
    };
  }

  // Delete original link
  changes[link.id] = { type: "deleted" };

  // Create link from source to new node
  const linkSourceToNewNode = generateLinkIDFromNodes(link.source, nodeId);
  changes[linkSourceToNewNode] = {
    item: {
      ...link,
      id: linkSourceToNewNode,
      target: nodeId,
    },
    type: "new",
  };

  // Create link from new node to target
  const linkNewNodeToTarget = generateLinkIDFromNodes(nodeId, link.target);
  changes[linkNewNodeToTarget] = {
    item: {
      ...link,
      id: linkNewNodeToTarget,
      source: nodeId,
    },
    type: "new",
  };

  store.dispatch(storePathsChanges({ changes, commitOptions }));
};

/**
 * Create a new node with a link to another node.
 *
 * @param nodeId The ID of the new node.
 * @param nodePosition The position of the new node in Geo coordinates.
 * @param floorId The ID of the floor where the new node is located.
 * @param originNodeId The ID of the node from which the link originates.
 */
export const createNodeWithLink = (
  nodeId: PathDictNodeItem["id"],
  nodePosition: { lat: number; lng: number },
  floorId: number,
  originNodeId: PathDictNodeItem["id"],
  commitOptions?: UndoableCommitOptions,
) => {
  const changes: EditorChanges<PathDictGenericItem> = {};
  const activePathsDict = selectActivePathsDict(store.getState());

  const currentBuilding = selectCurrentBuilding(store.getState());
  const cartesian =
    currentBuilding.coordinateConverterFlatEarth.toCartesian(nodePosition);

  // Create link
  const linkId = generateLinkIDFromNodes(originNodeId, nodeId);
  const linkFloorId =
    activePathsDict[originNodeId].floorId === floorId ? floorId : undefined;
  changes[linkId] = {
    item: {
      accessible: true,
      floorId: linkFloorId,
      id: generateLinkIDFromNodes(originNodeId, nodeId),
      origin: "both",
      source: originNodeId,
      tags: [],
      target: nodeId,
      type: "link",
    },
    type: "new",
  };

  // Create node
  changes[nodeId] = {
    item: {
      floorId: floorId,
      id: nodeId,
      lat: nodePosition.lat,
      lng: nodePosition.lng,
      type: "node",
      x: cartesian.x,
      y: cartesian.y,
    },
    type: "new",
  };

  store.dispatch(storePathsChanges({ changes, commitOptions }));
};

/**
 * Create a link joining two nodes
 *
 * @param sourceId The ID of the first node
 * @param targetId The ID of the second node
 * @param floorId The ID of the floor where the link should be created
 */
export const createLinkJoiningNodes = (
  sourceId: PathDictNodeItem["id"],
  targetId: PathDictNodeItem["id"],
  floorId: number | null,
  commitOptions?: UndoableCommitOptions,
) => {
  const linkId = generateLinkIDFromNodes(sourceId, targetId);
  store.dispatch(
    storePathsChanges({
      changes: {
        [linkId]: {
          item: {
            accessible: true,
            floorId: floorId,
            id: linkId,
            origin: "both",
            source: sourceId,
            tags: [],
            target: targetId,
            type: "link",
          },
          type: "new",
        },
      },
      commitOptions,
    }),
  );
};

/**
 * Rotate the paths of a building by a specified number of degrees.
 * The paths are rotated around the centroid of all nodes.
 *
 * @param degrees Rotation in degrees to be applied
 */
export const rotatePaths = (
  degrees: number,
  commitOptions?: UndoableCommitOptions,
) => {
  const currentBuilding = selectCurrentBuilding(store.getState());
  const pathsDict = selectActivePathsDict(store.getState());
  const featuresColl = featureCollection(
    Object.entries(pathsDict)
      .filter(([, item]) => item.type === "node")
      .map(([id, item]) =>
        point(
          [(item as PathDictNodeItem).lng, (item as PathDictNodeItem).lat],
          {
            id,
          },
        ),
      ),
  );

  // Calculate centroid
  const centroid = centerOfMass(featuresColl);

  // Rotate features
  const transformedFeaturesCollection = transformRotate(featuresColl, degrees, {
    pivot: centroid,
  });

  // Create the changes object based on the updated coords
  const changes = transformedFeaturesCollection.features.reduce(
    (acc, feature) => {
      const { id } = feature.properties;
      const [lng, lat] = feature.geometry.coordinates;
      const { x, y } = currentBuilding.coordinateConverterFlatEarth.toCartesian(
        {
          lat,
          lng,
        },
      );
      acc[id] = {
        item: { lat, lng, x, y },
        type: "updated",
      };
      return acc;
    },
    {} as EditorChanges<PathDictGenericItem>,
  );

  store.dispatch(storePathsChanges({ changes, commitOptions }));
};

/**
 * Scale the paths of a building by a specified scale factor.
 * The paths are scaled using the centroid of all nodes.
 *
 * @param scaleFactor Scale factor to be applied (1 = keep same scale)
 */
export const scalePaths = (
  scaleFactor: number,
  commitOptions?: UndoableCommitOptions,
) => {
  const currentBuilding = selectCurrentBuilding(store.getState());
  const pathsDict = selectActivePathsDict(store.getState());
  const featuresColl = featureCollection(
    Object.entries(pathsDict)
      .filter(([, item]) => item.type === "node")
      .map(([id, item]) =>
        point(
          [(item as PathDictNodeItem).lng, (item as PathDictNodeItem).lat],
          {
            id,
          },
        ),
      ),
  );

  // Calculate centroid
  const centroid = centerOfMass(featuresColl);

  // Scale features
  const transformedFeaturesCollection = transformScale(
    featuresColl,
    scaleFactor,
    {
      origin: centroid,
    },
  );

  // Create the changes object based on the updated coords
  const changes = transformedFeaturesCollection.features.reduce(
    (acc, feature) => {
      const { id } = feature.properties;
      const [lng, lat] = feature.geometry.coordinates;
      const { x, y } = currentBuilding.coordinateConverterFlatEarth.toCartesian(
        {
          lat,
          lng,
        },
      );
      acc[id] = {
        item: { lat, lng, x, y },
        type: "updated",
      };
      return acc;
    },
    {} as EditorChanges<PathDictGenericItem>,
  );

  store.dispatch(storePathsChanges({ changes, commitOptions }));
};

/**
 * Analyze the paths of a building for potential misconfigurations
 *
 * @param pathDict pathDict object
 * @param building building object
 * @param thresholdMeters threshold for considering two nodes "too near"
 * @param onlyOutside only calculate outside nodes and ignore the rest of metrics
 * @returns
 */
export const analyzePathProblems = (
  pathDict: PathDict,
  building: Building,
  thresholdMeters: number,
  onlyOutside: boolean = false,
): PathProblem[] => {
  const allItems = Object.values(pathDict);
  const nodes: PathDictNodeItem[] = [];
  const results: PathProblem[] = [];

  // First pass: collect nodes and immediate outside checks if onlyOutside is true
  if (onlyOutside) {
    for (const item of allItems) {
      if (item.type === "node") {
        if (!building.isPointInsideBounds(new LatLng(item.lat, item.lng))) {
          results.push({ ...item, problemType: "outside" });
        }
      }
    }
    return results; // Early return with only outside nodes
  }

  // Full processing when onlyOutside is false
  const links: PathDictLinkItem[] = [];
  for (const item of allItems) {
    if (item.type === "node") nodes.push(item);
    if (item.type === "link") links.push(item);
  }

  if (nodes.length === 0) return results;

  // Track connected nodes for isolation check
  const connectedNodes = new Set<PathDictNodeItem["id"]>();
  for (const link of links) {
    connectedNodes.add(link.source);
    connectedNodes.add(link.target);
  }

  // Group nodes by floorId
  const nodesByFloor = new Map<Floor["id"] | null, PathDictNodeItem[]>();
  for (const node of nodes) {
    const floorNodes = nodesByFloor.get(node.floorId) || [];
    floorNodes.push(node);
    nodesByFloor.set(node.floorId, floorNodes);
  }

  const nearNodeIds = new Set<PathDictNodeItem["id"]>();
  for (const [_floorId, floorNodes] of nodesByFloor) {
    const sortedFloorNodes = [...floorNodes].sort((a, b) => a.lng - b.lng);

    for (let i = 0; i < sortedFloorNodes.length; i++) {
      const node = sortedFloorNodes[i];

      // If node has no links, raise an "isolated" problem
      if (!connectedNodes.has(node.id)) {
        results.push({ ...node, problemType: "isolated" });
      }

      // If node is outside the bounds of the building, raise an "outside" problem
      if (!building.isPointInsideBounds(new LatLng(node.lat, node.lng))) {
        results.push({ ...node, problemType: "outside" });
      }

      // Iterate through the closest nodes to check if any is closer
      // than the configured threshold
      if (thresholdMeters > 0 && !nearNodeIds.has(node.id)) {
        for (
          let j = i + 1;
          j < sortedFloorNodes.length &&
          // Computationally easy check to filter out far away nodes
          // 0.00005 represents APROX 5meters
          sortedFloorNodes[j].lng - node.lng < 0.00005;
          j++
        ) {
          const dist = distance(
            [node.lng, node.lat],
            [sortedFloorNodes[j].lng, sortedFloorNodes[j].lat],
            { units: "meters" },
          );

          if (dist < thresholdMeters) {
            results.push({ ...node, problemType: "near" });
            nearNodeIds.add(node.id);
            break;
          }
        }
      }
    }
  }

  // Return sorted by severity
  return results.sort((a, b) => {
    if (a.problemType === b.problemType) return parseInt(a.id) - parseInt(b.id);
    const priority = { isolated: 1, near: 2, outside: 0 };
    return priority[a.problemType] - priority[b.problemType];
  });
};

/**
 * Selects a given path feature and focuses the map in its position
 */
export const focusItem = (itemId: PathDictGenericItem["id"]) => {
  const pathsDict = selectActivePathsDict(store.getState());
  const item = pathsDict[itemId];
  if (!item || item.type !== "node") return;
  store.dispatch(setPathsEditorSelectedFeatureId(item.id));

  // Change to this item floor id
  if (item.floorId) {
    store.dispatch(setCurrentFloorID(item.floorId));
  }

  // Center map on the item
  updateMapViewState({
    latitude: item.lat,
    longitude: item.lng,
    zoom: 21,
  });
};

/**
 * Convert the current active paths dictionary to Situm's API
 * compatible format, ready to be uploaded
 */
export const getPathsDictAPIFormat = () => {
  const pathsDict = selectActivePathsDict(store.getState());
  const building = selectCurrentBuilding(store.getState());
  return pathDictToPaths(building, pathsDict);
};
